import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, i as isTriggerEvent } from './utils.js';
import { d as defineCustomElement$1 } from './cl-icon2.js';

// Constants
const component = 'cl-icon-button';
const iconButtonConstants = {
  TYPE: {
    BUTTON: 'button',
    SUBMIT: 'submit',
    RESET: 'reset',
  },
  VARIANT: {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    TERTIARY: 'tertiary',
  },
};
// Type-validators
const iconButtonValidator = {
  type: createTypeValidator(iconButtonConstants.TYPE),
  variant: createTypeValidator(iconButtonConstants.VARIANT),
};

const clIconButtonCss = ".sc-cl-icon-button-h{--background-color:rgb(var(--color-brand-main));--border-color:rgb(var(--color-brand-main));--color:rgb(var(--font-color-onlight-brand));border-radius:var(--border-radius-circle);display:inline-block}.sc-cl-icon-button-h .icon-button.sc-cl-icon-button{background-color:var(--button-background-color, var(--background-color));border-color:var(--button-border-color, var(--border-color));color:var(--button-color, var(--color))}.sc-cl-icon-button-h .icon-button.sc-cl-icon-button slot-fb.sc-cl-icon-button:only-child{display:flex}.sc-cl-icon-button-h .icon-button.sc-cl-icon-button{border-radius:var(--border-radius-circle);height:var(--spacing-5);width:var(--spacing-5)}.sc-cl-icon-button-h .icon-button--primary.sc-cl-icon-button{--background-color:rgb(var(--color-brand-main));--border-color:transparent;--color:rgb(var(--font-color-onbrand-default))}.sc-cl-icon-button-h .icon-button--primary.sc-cl-icon-button:hover{--background-color:rgba(var(--color-brand-main), var(--opacity-4))}.sc-cl-icon-button-h .icon-button--primary.sc-cl-icon-button:focus-visible{--button-border-color:rgb(var(--system-color-focus))}.sc-cl-icon-button-h .icon-button--primary.sc-cl-icon-button:active{--background-color:rgb(var(--color-brand-200));--button-border-color:transparent;--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--primary.icon-button--selected.sc-cl-icon-button:not(.icon-button--disabled){--background-color:rgb(var(--surface-color-main));--border-color:rgb(var(--color-brand-main));--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--primary.icon-button--disabled.sc-cl-icon-button{--background-color:rgba(var(--color-neutral-900), var(--opacity-1));--color:rgba(var(--color-neutral-900), var(--opacity-3))}.sc-cl-icon-button-h .icon-button--secondary.sc-cl-icon-button{--background-color:transparent;--border-color:rgb(var(--color-brand-600));--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--secondary.sc-cl-icon-button:hover{--border-color:rgba(var(--color-brand-main), var(--opacity-4));--color:rgba(var(--font-color-onlight-brand), var(--opacity-4))}.sc-cl-icon-button-h .icon-button--secondary.sc-cl-icon-button:focus-visible{--button-border-color:rgb(var(--system-color-focus))}.sc-cl-icon-button-h .icon-button--secondary.sc-cl-icon-button:active{--background-color:rgb(var(--color-brand-200));--button-border-color:transparent;--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--secondary.icon-button--selected.sc-cl-icon-button:not(.icon-button--disabled){--background-color:rgb(var(--color-brand-main));--border-color:transparent;--color:rgb(var(--color-neutral-50s))}.sc-cl-icon-button-h .icon-button--secondary.icon-button--disabled.sc-cl-icon-button{--border-color:rgba(var(--color-neutral-900), var(--opacity-2));--color:rgba(var(--color-neutral-900), var(--opacity-3))}.sc-cl-icon-button-h .icon-button--tertiary.sc-cl-icon-button{--background-color:transparent;--border-color:transparent;--color:rgb(var(--color-neutral-900))}.sc-cl-icon-button-h .icon-button--tertiary.sc-cl-icon-button:hover{--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--tertiary.sc-cl-icon-button:active{--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--tertiary.sc-cl-icon-button:focus-visible{--color:rgb(var(--system-color-focus))}.sc-cl-icon-button-h .icon-button--tertiary.icon-button--selected.sc-cl-icon-button:not(.icon-button--disabled){--color:rgb(var(--font-color-onlight-brand))}.sc-cl-icon-button-h .icon-button--tertiary.icon-button--disabled.sc-cl-icon-button{--color:rgba(var(--color-neutral-900), var(--opacity-3))}.sc-cl-icon-button-h .icon-button.icon-button--small.sc-cl-icon-button{height:var(--spacing-3);padding:0;width:var(--spacing-3)}.sc-cl-icon-button-h .icon-button.icon-button--small.sc-cl-icon-button slot-fb.sc-cl-icon-button cl-icon.sc-cl-icon-button,.sc-cl-icon-button-h .icon-button.icon-button--small.sc-cl-icon-button-s>cl-icon{--icon-size:calc(var(--spacing-2) + var(--spacing-quarter))}.sc-cl-icon-button-h .icon-button.sc-cl-icon-button{align-items:center;border-style:solid;cursor:pointer;display:flex;justify-content:center;outline:none}.sc-cl-icon-button-h .icon-button--disabled.sc-cl-icon-button{cursor:default;pointer-events:none}";

const ClIconButton = /*@__PURE__*/ proxyCustomElement(class ClIconButton extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Handles the tigger events
     */
    this.handleTrigger = (event) => {
      if (isTriggerEvent(event) && this.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.disabled = false;
    this.iconName = '';
    this.selected = false;
    this.small = false;
    this.type = 'button';
    this.variant = 'primary';
  }
  /**
   * Validates the type property
   */
  handleType(type) {
    if (!iconButtonValidator.type(type)) {
      this.logger.error.attribute.type(`Given invalid value "${type}".`);
    }
  }
  /**
   * Validates the variant property
   */
  handleVariant(variant) {
    if (!iconButtonValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Validate "type" attribute
   * - Validate "variant" atrribute
   */
  connectedCallback() {
    this.handleType(this.type);
    this.handleVariant(this.variant);
  }
  render() {
    const { disabled, iconName, selected, small, type, variant } = this;
    return (h(Host, { tabIndex: -1, onClick: this.handleTrigger, onKeyPress: this.handleTrigger }, h("button", { class: cl(`icon-button icon-button--${variant}`, {
        'icon-button--selected': selected,
        'icon-button--disabled': disabled,
        'icon-button--small': small,
      }), disabled: disabled, type: type }, h("slot", null, h("cl-icon", { name: iconName })))));
  }
  static get watchers() { return {
    "type": ["handleType"],
    "variant": ["handleVariant"]
  }; }
  static get style() { return clIconButtonCss; }
}, [6, "cl-icon-button", {
    "disabled": [4],
    "iconName": [1, "icon-name"],
    "selected": [516],
    "small": [4],
    "type": [1],
    "variant": [1]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-icon-button", "cl-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-icon-button":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClIconButton);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClIconButton as C, defineCustomElement as d };

//# sourceMappingURL=cl-icon-button2.js.map